import React from "react";
import Reveal from "@/components/sections/Reveal";
import AdvisoryForm from "@/components/forms/AdvisoryForm";
import { useSiteImages } from "@/lib/useSiteImages";

const engagements = [
  {
    title: "The Clarity Sprint",
    duration: "30 days",
    body: "A focused 30-day engagement for founders who need to convert technical ambiguity into a funder-ready position. Covers: tech stack audit, architecture decisions, roadmap structure, and documentation the team can execute against.",
    outcome: "Developer-ready MVP specification. Investment-grade technical documentation.",
  },
  {
    title: "The 90-Day Arc",
    duration: "90 days",
    body: "A structured 90-day fractional CTO/CIO engagement. For organizations navigating a significant build, raise, or technology leadership transition.",
    outcome:
      "Governance documentation. Architecture decisions made and recorded. Team capability advanced. Rakia exits as planned — you hold the full operating record.",
  },
  {
    title: "The Advisory Circle",
    duration: "Ongoing",
    body: "A private room for founders and senior operators who need a standing thought partner on high-stakes technology decisions. Limited membership. Direct access. Monthly structured session plus async advisory.",
    outcome:
      "You make better decisions faster, with someone who has made similar ones at scale.",
  },
];

export default function Advisory() {
  const IMAGES = useSiteImages();
  return (
    <div>
      {/* HERO */}
      <section
        className="rk-section"
        style={{ paddingTop: "calc(var(--rk-section-py) + 60px)" }}
      >
        <div className="rk-container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="rk-eyebrow">Advisory — Fractional CTO/CIO</div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="rk-heading-page mt-5">
                  The work, not the advice.
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="mt-7 rk-body"
                  style={{ maxWidth: 580, color: "var(--rk-muted)" }}
                >
                  Operating relationships, not retainers. Led by Rakia
                  personally. A limited number of engagements each quarter.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.08} y={28}>
                <figure
                  style={{
                    position: "relative",
                    aspectRatio: "4/5",
                    overflow: "hidden",
                    borderRadius: 2,
                    border: "1px solid var(--rk-border)",
                  }}
                >
                  <img
                    src={IMAGES.advisoryPortrait}
                    alt="Rakia Finley with audience at a leadership session"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      filter: "saturate(0.96) contrast(1.02)",
                    }}
                    loading="eager"
                  />
                </figure>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section
        className="rk-section-tight"
        style={{
          backgroundColor: "var(--rk-dark)",
          color: "var(--rk-cream)",
        }}
      >
        <div className="rk-container-text">
          <Reveal>
            <div
              className="rk-eyebrow"
              style={{ color: "rgba(250,247,242,0.55)" }}
            >
              Pull quote
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <blockquote
              className="mt-6"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(28px, 3.6vw, 48px)",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
                color: "var(--rk-cream)",
              }}
            >
              “Every client I take gets me — not a team I assembled last week.
              That is the model. That is the standard.”
            </blockquote>
          </Reveal>
          <Reveal delay={0.1}>
            <div
              className="mt-7 rk-caption"
              style={{ color: "rgba(250,247,242,0.6)" }}
            >
              — Rakia Finley
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT IT IS */}
      <section className="rk-section">
        <div className="rk-container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <Reveal>
                <div className="rk-eyebrow">The model</div>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="rk-heading-section mt-5">
                  What the Advisory is.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.05}>
                <p className="rk-body" style={{ color: "var(--rk-ink)" }}>
                  Copper & Vine’s fractional engagements are not retainers.
                  They are operating relationships — structured,
                  time-bounded, and led by Rakia personally.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-5 rk-body" style={{ color: "var(--rk-ink)" }}>
                  She takes a limited number of engagements each quarter. Work
                  begins with a scoped conversation, not a proposal.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ENGAGEMENT CARDS */}
      <section
        className="rk-section"
        style={{ backgroundColor: "var(--rk-surface)" }}
      >
        <div className="rk-container-wide">
          <Reveal>
            <div className="rk-eyebrow">Engagement formats</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="rk-heading-section mt-5">Three ways in.</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {engagements.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.05}>
                <article
                  className="rk-card h-full flex flex-col"
                  style={{ backgroundColor: "var(--rk-bg)" }}
                >
                  <div className="flex items-baseline justify-between">
                    <span
                      className="rk-caption"
                      style={{ color: "var(--rk-copper)" }}
                    >
                      {e.duration}
                    </span>
                    <span
                      className="rk-caption"
                      style={{ color: "var(--rk-muted)" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3
                    className="mt-4 pb-4"
                    style={{
                      borderBottom: "1px solid var(--rk-copper)",
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(26px, 2.4vw, 32px)",
                      lineHeight: 1.1,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {e.title}
                  </h3>
                  <p
                    className="mt-5 rk-body"
                    style={{ color: "var(--rk-ink)", flex: 1 }}
                  >
                    {e.body}
                  </p>
                  <div className="mt-7">
                    <div className="rk-eyebrow">Outcome</div>
                    <p
                      className="mt-3"
                      style={{
                        color: "var(--rk-ink)",
                        fontSize: 16,
                        lineHeight: 1.55,
                      }}
                    >
                      {e.outcome}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOT FOR */}
      <section className="rk-section">
        <div className="rk-container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <Reveal>
                <div className="rk-eyebrow" style={{ color: "var(--rk-copper)" }}>
                  A note on fit
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="rk-heading-section mt-5">
                  Who this is not for.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.05}>
                <p
                  className="rk-body"
                  style={{ color: "var(--rk-ink)", fontSize: "clamp(18px, 1.3vw, 21px)", lineHeight: 1.55 }}
                >
                  Rakia does not take exploratory retainers, unpaid advisory
                  roles, or engagements where the decision-maker is not in the
                  room.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="mt-6 rk-body"
                  style={{ color: "var(--rk-ink)", fontSize: "clamp(18px, 1.3vw, 21px)", lineHeight: 1.55 }}
                >
                  If you are still deciding whether you need a fractional CTO,
                  the answer is usually yes — but this is not where that
                  conversation starts. Start with the Operator Packet at
                  {" "}
                  <a
                    href="https://coppervine.io"
                    target="_blank"
                    rel="noreferrer"
                    className="rk-link"
                  >
                    coppervine.io
                  </a>
                  .
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section
        className="rk-section"
        style={{ backgroundColor: "var(--rk-surface)" }}
      >
        <div className="rk-container-text">
          <Reveal>
            <div className="rk-eyebrow">Inquiry</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="rk-heading-section mt-5">Send Rakia a note.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 rk-body" style={{ color: "var(--rk-ink)" }}>
              The fields below are the start of the conversation. Three lines
              is plenty.
            </p>
          </Reveal>
          <div
            className="mt-12 p-6 md:p-10"
            style={{
              backgroundColor: "var(--rk-bg)",
              border: "1px solid var(--rk-copper)",
              borderRadius: 2,
            }}
          >
            <AdvisoryForm />
          </div>
        </div>
      </section>
    </div>
  );
}
